'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { cn } from '../../../lib/utils'
import Icon, { iconList, type iconTypes } from '../Icon'
import { Button } from '../Button'
import { ResponsivePopover } from '../ResponsivePopover'
import { IconSelector } from '../Icon/IconSelector'
import type { SidebarNavigationMeta } from '../SideNavigationV2/SideBar/SidebarNavigationDrag'

import type { PageTypeaheadResult } from '../PageTypeaheadPopover'

export type WorkspaceNavItemMenuConfig = {
  pageTypeahead?: {
    title?: string
    placeholder?: string
    emptyMessage?: string
    onSearch: (query: string) => Promise<PageTypeaheadResult[]>
    onSelect: (page: PageTypeaheadResult) => void | Promise<void>
    onCreateNewPage?: () => void
    createNewPageLabel?: string
  }
  onSearchPagesUnderItem?: (
    anchorNavItemId: string,
    query: string,
  ) => Promise<PageTypeaheadResult[]>
  onAddPageUnderItem?: (
    anchorNavItemId: string,
    page: PageTypeaheadResult,
  ) => void | Promise<void>
}

export type SidebarNavigationEditContextValue = {
  enabled: boolean
  onUpdate: (input: {
    id: string
    label: string
    icon: iconTypes | null
  }) => void | Promise<void>
  onDelete: (id: string) => void | Promise<void>
  itemMenu?: WorkspaceNavItemMenuConfig
}

const SidebarNavigationEditContext =
  createContext<SidebarNavigationEditContextValue>({
    enabled: false,
    onUpdate: async () => undefined,
    onDelete: async () => undefined,
    itemMenu: undefined,
  })

export function SidebarNavigationEditProvider({
  enabled,
  onUpdate,
  onDelete,
  itemMenu,
  children,
}: {
  enabled: boolean
  onUpdate: SidebarNavigationEditContextValue['onUpdate']
  onDelete: SidebarNavigationEditContextValue['onDelete']
  itemMenu?: WorkspaceNavItemMenuConfig
  children: React.ReactNode
}) {
  const value = useMemo(
    () => ({
      enabled,
      onUpdate,
      onDelete,
      itemMenu,
    }),
    [enabled, itemMenu, onDelete, onUpdate],
  )

  return (
    <SidebarNavigationEditContext.Provider value={value}>
      {children}
    </SidebarNavigationEditContext.Provider>
  )
}

export function useSidebarNavigationEdit() {
  return useContext(SidebarNavigationEditContext)
}

export type WorkspaceNavItemEditDrawerProps = {
  item: SidebarNavigationMeta & {
    label: string
    icon?: string | null
  }
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger: React.ReactNode
  canDelete?: boolean
  onSave: (input: {
    id: string
    label: string
    icon: iconTypes | null
  }) => void | Promise<void>
  onDelete: (id: string) => void | Promise<void>
  showIconEditor?: boolean
}

const kindLabel: Record<SidebarNavigationMeta['kind'], string> = {
  section: 'Section',
  group: 'Group',
  link: 'Page',
}

const resolveNavItemIcon = (icon?: string | null): iconTypes | null => {
  if (!icon) return null
  return iconList.includes(icon as iconTypes) ? (icon as iconTypes) : null
}

export function WorkspaceNavItemEditForm({
  item,
  open,
  canDelete = true,
  showIconEditor = true,
  onSave,
  onDelete,
  onClose,
}: {
  item: SidebarNavigationMeta & { label: string; icon?: string | null }
  open: boolean
  canDelete?: boolean
  showIconEditor?: boolean
  onSave: WorkspaceNavItemEditDrawerProps['onSave']
  onDelete: WorkspaceNavItemEditDrawerProps['onDelete']
  onClose: () => void
}) {
  const [label, setLabel] = useState(item.label)
  const [icon, setIcon] = useState<iconTypes | null>(
    resolveNavItemIcon(item.icon),
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (!open) return
    setLabel(item.label)
    setIcon(resolveNavItemIcon(item.icon))
  }, [open, item.label, item.icon])

  const handleSave = useCallback(async () => {
    const trimmed = label.trim()
    if (!trimmed || isSaving) return

    setIsSaving(true)
    try {
      await onSave({
        id: item.id,
        label: trimmed,
        icon: showIconEditor ? icon : resolveNavItemIcon(item.icon),
      })
      onClose()
    } finally {
      setIsSaving(false)
    }
  }, [icon, isSaving, item.id, label, onClose, onSave, showIconEditor])

  const handleDelete = useCallback(async () => {
    if (!canDelete || isDeleting) return

    setIsDeleting(true)
    try {
      await onDelete(item.id)
      onClose()
    } finally {
      setIsDeleting(false)
    }
  }, [canDelete, isDeleting, item.id, onDelete, onClose])

  if (!open) return null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`nav-item-label-${item.id}`}
          className="text-xs font-medium text-muted-foreground"
        >
          Name
        </label>
        <input
          id={`nav-item-label-${item.id}`}
          type="text"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          disabled={isSaving || isDeleting}
          className={cn(
            'h-9 w-full rounded-md border border-border/60 bg-background px-3',
            'text-sm text-foreground placeholder:text-muted-foreground/50',
            'outline-none focus-visible:ring-1 focus-visible:ring-ring',
          )}
        />
      </div>

      {showIconEditor ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            Icon
          </span>
          <IconSelector
            value={icon}
            onValueChange={setIcon}
            buttonClassName="h-9 w-full justify-start"
            isDisabled={isSaving || isDeleting}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          className="w-full"
          label={isSaving ? 'Saving…' : 'Save changes'}
          disabled={!label.trim() || isSaving || isDeleting}
          handleClick={() => void handleSave()}
        />
        {canDelete ? (
          <Button
            type="button"
            size="sm"
            variant="destructive"
            className="w-full"
            label={isDeleting ? 'Deleting…' : 'Delete'}
            disabled={isSaving || isDeleting}
            handleClick={() => void handleDelete()}
          />
        ) : null}
      </div>
    </div>
  )
}

export function WorkspaceNavItemEditDrawer({
  item,
  open,
  onOpenChange,
  trigger,
  canDelete = true,
  showIconEditor = true,
  onSave,
  onDelete,
}: WorkspaceNavItemEditDrawerProps) {
  return (
    <ResponsivePopover
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      title={`Edit ${kindLabel[item.kind].toLowerCase()}`}
      popoverAlign="end"
      popoverClassName="w-72 p-4"
      drawerClassName="px-0"
    >
      <WorkspaceNavItemEditForm
        item={item}
        open={open}
        canDelete={canDelete}
        showIconEditor={showIconEditor}
        onSave={onSave}
        onDelete={onDelete}
        onClose={() => onOpenChange(false)}
      />
    </ResponsivePopover>
  )
}

export const SidebarNavItemEditTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<'button'> & {
    ariaLabel?: string
  }
>(function SidebarNavItemEditTrigger(
  {
    ariaLabel = 'Edit navigation item',
    className,
    onClick,
    onMouseDown,
    onPointerDown,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={ariaLabel}
      data-sidebar="menu-action"
      onClick={(event) => {
        event.stopPropagation()
        onClick?.(event)
      }}
      onMouseDown={(event) => {
        event.stopPropagation()
        onMouseDown?.(event)
      }}
      onPointerDown={(event) => {
        event.stopPropagation()
        onPointerDown?.(event)
      }}
      className={cn(
        'text-muted-foreground/70 hover:bg-accent/50 hover:text-foreground',
        'absolute top-1/2 right-1 z-10 flex size-5 -translate-y-1/2 items-center justify-center rounded-md',
        'opacity-0 transition-opacity group-hover/nav-row:opacity-100 group-focus-within/nav-row:opacity-100',
        'group-hover/section-header:opacity-100 group-focus-within/section-header:opacity-100',
        className,
      )}
      {...props}
    >
      <Icon name="Ellipsis" className="size-3.5" />
    </button>
  )
})

SidebarNavItemEditTrigger.displayName = 'SidebarNavItemEditTrigger'

export function SidebarNavItemEditControl({
  item,
  label,
  icon,
  canDelete = true,
  triggerClassName,
}: {
  item: SidebarNavigationMeta
  label: string
  icon?: string | null
  canDelete?: boolean
  triggerClassName?: string
}) {
  const { enabled, onUpdate, onDelete } = useSidebarNavigationEdit()
  const [open, setOpen] = useState(false)

  if (!enabled) return null

  return (
    <WorkspaceNavItemEditDrawer
      item={{ ...item, label, icon: icon ?? item.icon ?? undefined }}
      open={open}
      onOpenChange={setOpen}
      canDelete={canDelete}
      onSave={onUpdate}
      onDelete={onDelete}
      trigger={<SidebarNavItemEditTrigger className={triggerClassName} />}
    />
  )
}
